import { useState, useEffect, useRef } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { ImageDetails, ImageDetailsModal, ImageType } from '../../../shared/components/ImageDetailsModal';
import { ImageFile, ImageUploader } from '../../../shared/components/ImageUploader';
import { useMediaUpload } from '../../../shared/hooks/useMediaUpload';
import { MediaFile, MediaType } from '../../../shared/types/media.types';
import { IImageData } from '../types/test-detail.types';

interface PassageImageManagerProps {
  images: IImageData[];
  onImagesChange: (images: IImageData[]) => void;
  testId?: string;
}

export const PassageImageManager = ({ images, onImagesChange, testId }: PassageImageManagerProps) => {
  const { uploadMedia } = useMediaUpload({
    testId,
    maxSize: 10 * 1024 * 1024, // 10MB
    acceptedTypes: ['image/*']
  });
  const [selectedImage, setSelectedImage] = useState<ImageFile | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingImageIndex, setEditingImageIndex] = useState<number | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);

  // Handle image selection from uploader
  const handleImageSelect = (image: ImageFile) => {
    console.log('Image selected:', image);
    setUploadError(null);

    // Check if we have an uploaded URL or just a preview
    if (image.uploadedUrl) {
      console.log('Image already uploaded:', image.uploadedUrl);
    }

    // Store the selected image and open the modal for details
    setSelectedImage(image);
    setIsModalOpen(true);
  };

  // Handle image details submission
  const handleImageDetailsSubmit = (details: ImageDetails) => {
    if (editingImageIndex !== null) {
      // Update existing image
      const updatedImages = [...images];
      updatedImages[editingImageIndex] = {
        ...updatedImages[editingImageIndex],
        type: details.type as any,
        title: details.title,
        caption: details.caption
      };
      onImagesChange(updatedImages);
      setEditingImageIndex(null);
    } else if (selectedImage) {
      try {
        // Get the URL - either the uploaded one or the preview
        const imageUrl = selectedImage.uploadedUrl || selectedImage.previewUrl;

        if (!imageUrl) {
          throw new Error('No image URL available');
        }

        console.log('Using image URL:', imageUrl);

        // Create a new image object with the details
        const newImage: IImageData = {
          id: selectedImage.id || uuidv4(),
          type: details.type as any,
          title: details.title,
          caption: details.caption,
          storageUrl: imageUrl,
          fileName: selectedImage.file.name
        };

        console.log('Adding new image:', newImage);
        onImagesChange([...images, newImage]);
        setSelectedImage(null);
      } catch (error) {
        console.error('Error adding image:', error);
        setUploadError(error instanceof Error ? error.message : 'Error adding image');
      }
    }

    setIsModalOpen(false);
  };

  // Handle image deletion
  const handleDeleteImage = (index: number) => {
    const updatedImages = [...images];
    updatedImages.splice(index, 1);
    onImagesChange(updatedImages);
  };

  // Handle image edit
  const handleEditImage = (index: number) => {
    console.log('Editing image at index:', index, images[index]);
    setEditingImageIndex(index);
    setIsModalOpen(true);
  };

  // Close modal
  const handleModalClose = () => {
    setIsModalOpen(false);
    setSelectedImage(null);
    setEditingImageIndex(null);
  };


  // Reference to the component container
  const containerRef = useRef<HTMLDivElement>(null);

  // Effect to add paste event listener
  useEffect(() => {
    // Handler for paste events
    const handlePasteEvent = (event: ClipboardEvent) => {
      // Only process if the paste happened within our component or its children
      if (containerRef.current?.contains(event.target as Node)) {
        // Prevent default paste behavior if it's in an input or textarea
        if ((event.target as HTMLElement).tagName !== 'INPUT' && 
            (event.target as HTMLElement).tagName !== 'TEXTAREA') {
          event.preventDefault();
        }
        
        // Process the paste event
        if (event.clipboardData) {
          const items = event.clipboardData.items;
          
          for (let i = 0; i < items.length; i++) {
            if (items[i].type.indexOf('image') !== -1) {
              // We found an image in the clipboard
              const blob = items[i].getAsFile();
              if (blob) {
                processPastedImage(blob);
              }
              return;
            }
          }
        }
      }
    };

    // Add the event listener
    document.addEventListener('paste', handlePasteEvent);

    // Clean up the event listener when component unmounts
    return () => {
      document.removeEventListener('paste', handlePasteEvent);
    };
  }, []);

  // Process a pasted image file
  const processPastedImage = (blob: File) => {
    try {
      setUploadError(null);
      
      // Create a file from the blob
      const fileName = `pasted-image-${new Date().getTime()}.png`;
      const file = new File([blob], fileName, { type: blob.type });
      
      // Create preview URL
      const preview = URL.createObjectURL(file);
      
      // Create image file object
      const imageFile: ImageFile = {
        file,
        id: uuidv4(),
        previewUrl: preview,
        progress: 0
      };
      
      // Handle the image as if it was selected from uploader
      handleImageSelect(imageFile);
    } catch (error) {
      console.error('Error processing pasted image:', error);
      setUploadError(error instanceof Error ? error.message : 'Failed to process pasted image');
    }
  };

  // Handle paste from clipboard button click
  const handlePasteImage = async () => {
    try {
      setUploadError(null);
      setIsUploading(true);
      
      // Try to read clipboard data
      const items = await navigator.clipboard.read();
      
      // Find image items in clipboard
      for (const item of items) {
        for (const type of item.types) {
          // Check if the item is an image
          if (type.startsWith('image/')) {
            // Get the image blob
            const blob = await item.getType(type);
            
            // Create a file from the blob
            const fileName = `pasted-image-${new Date().getTime()}.${type.split('/')[1] || 'png'}`;
            const file = new File([blob], fileName, { type });
            
            // Create MediaFile object for upload
            const mediaFile: MediaFile = {
              file,
              preview: URL.createObjectURL(blob),
              type: MediaType.IMAGE,
              progress: 0
            };
            
            // Upload the image directly
            const imageUrl = await uploadMedia(mediaFile);
            
            // Create a new image data object
            const newImage: IImageData = {
              id: uuidv4(),
              storageUrl: imageUrl,
              type: 'image',
              title: '',
              caption: '',
              fileName: fileName
            };
            
            // Add the new image to the images array
            onImagesChange([...images, newImage]);
            
            // Open modal for editing details
            setEditingImageIndex(images.length);
            setIsModalOpen(true);
            return;
          }
        }
      }
      
      // If we get here, no image was found in clipboard
      setUploadError('No image found in clipboard');
    } catch (error) {
      console.error('Error pasting image from clipboard:', error);
      setUploadError(error instanceof Error ? error.message : 'Failed to paste image from clipboard');
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="mt-4" ref={containerRef}>
      <div className="flex justify-between items-center mb-2">
        <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">
          Passage Images
        </h3>
        <button
          type="button"
          onClick={handlePasteImage}
          disabled={isUploading}
          className="inline-flex items-center px-2.5 py-1.5 border border-gray-300 dark:border-gray-600 shadow-sm text-xs font-medium rounded text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 dark:focus:ring-offset-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
          title="Paste image from clipboard"
        >
          {isUploading ? (
            <>
              <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-indigo-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Uploading image...
            </>
          ) : (
            <>
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
              Paste Image
            </>
          )}
        </button>
      </div>

      {/* Image List */}
      {images.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
          {images.map((image, index) => (
            <div
              key={image.id || index}
              className="border rounded-lg p-3 bg-white dark:bg-gray-800 shadow-sm"
            >
              <div className="relative pb-[60%] mb-2 bg-gray-100 dark:bg-gray-700 rounded overflow-hidden">
                {image.storageUrl && (
                  <img
                    src={image.storageUrl}
                    alt={image.title || 'Passage image'}
                    className="absolute inset-0 w-full h-full object-cover"
                    onError={(e) => {
                      console.error('Image failed to load:', image.storageUrl);
                      // Provide a fallback image or placeholder
                      e.currentTarget.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgZmlsbD0iI2VlZWVlZSIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMjAiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGZpbGw9IiM5OTk5OTkiPkltYWdlIG5vdCBhdmFpbGFibGU8L3RleHQ+PC9zdmc+';
                    }}
                  />
                )}
                <div className="absolute top-2 right-2 flex space-x-1">
                  <button
                    type="button"
                    onClick={() => handleEditImage(index)}
                    className="p-1 bg-white dark:bg-gray-800 rounded-full shadow-sm text-gray-600 dark:text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400"
                    title="Edit"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"></path>
                    </svg>
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDeleteImage(index)}
                    className="p-1 bg-white dark:bg-gray-800 rounded-full shadow-sm text-gray-600 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400"
                    title="Delete"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
                    </svg>
                  </button>
                </div>
              </div>
              <div className="text-xs">
                <div className="font-medium text-gray-900 dark:text-white mb-1 truncate">
                  {image.title || ''}
                </div>
                <div className="text-gray-500 dark:text-gray-400 mb-1">
                  Type: {image.type || 'image'}
                </div>
                {image.caption && (
                  <div className="text-gray-600 dark:text-gray-300 line-clamp-2">
                    {image.caption}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Image Uploader */}
      <ImageUploader
        onImageSelect={handleImageSelect}
        dropzoneText={{
          title: 'Add an image',
          subtitle: 'or drag and drop',
          sizeInfo: 'Images up to 10MB'
        }}
        uploadMedia={uploadMedia}
      />

      {/* Error message */}
      {uploadError && (
        <div className="mt-2 text-sm text-red-500">
          {uploadError}
        </div>
      )}

      {/* Image Details Modal */}
      <ImageDetailsModal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        onSubmit={handleImageDetailsSubmit}
        fileName={selectedImage?.file.name || ''}
        initialDetails={editingImageIndex !== null ? {
          type: images[editingImageIndex]?.type as ImageType || 'image',
          title: images[editingImageIndex]?.title || '',
          caption: images[editingImageIndex]?.caption || ''
        } : undefined}
      />
    </div>
  );
};
